import { JucyIntegrationScreen } from "@/app/Settings/Integrations/Jucy/Screens/JucyIntegrationScreen";
import { useIntegrationRecord } from "@/app/Settings/Settings/Hooks/useIntegrationRecord";
import { IntegrationType } from "@/gql/graphql";
import { CampermanIntegrationScreen } from "@/app/Settings/Integrations/Camperman/CampermanIntegrationScreen";
import { CampermanGapFillerIntegrationScreen } from "@/app/Settings/Integrations/CampermanGapFiller/CampermanGapFillerIntegrationScreen";
import { GoogleSheetsIntegrationScreen } from "@/app/Settings/Integrations/GoogleSheets/GoogleSheetsIntegrationScreen";
import { RoadsurferIntegrationScreen } from "@/app/Settings/Integrations/Roadsurfer/RoadsurferIntegrationScreen";
import { Click4WheelsIntegrationScreen } from "@/app/Settings/Integrations/Click4Wheels/Click4WheelsIntegrationScreen";
import { RcmIntegrationScreen } from "@/app/Settings/Integrations/Rcm/Screens/RcmIntegrationScreen";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/catalyst/button";
import { TrashIcon } from "@heroicons/react/24/outline";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { deleteIntegrationrMutation } from "@/app/Settings/Settings/GraphQL/integrationMutations";
import { integrations } from "@/app/Settings/Integrations/Common/integrations";
import { useNavigate } from "@tanstack/react-router";
import { useConfirmDeleteDialog } from "@/lib/Components/Dialog/Hooks/useConfirmDeleteDialog";

export function SettingsIntegrationScreen() {
  const integration = useIntegrationRecord();
  const navigate = useNavigate();
  const { mutateAsync } = useGqlMutation(deleteIntegrationrMutation);
  const { open: openConfirmDelete } = useConfirmDeleteDialog();
  const meta = integrations.find((i) => i.type === integration.type);
  const name = meta?.name ?? "Integration";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                from="/$tenant/$orgId/supplier/{-$supplierId}"
                to="/$tenant/$orgId/supplier/{-$supplierId}/settings/integrations"
                params={(prev) => prev}
                search={(prev) => prev}
              >
                Integrations
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{name}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <Button
          color="red"
          onClick={() => {
            openConfirmDelete({
              title: `Delete ${name} integration`,
              message: `Are you sure you want to delete the ${name} integration? This cannot be undone.`,
              buttonText: "Delete",
              onDelete: async () => {
                await mutateAsync({ id: integration.id });
                await navigate({
                  from: "/$tenant/$orgId/supplier/{-$supplierId}",
                  to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/integrations",
                  params: (prev) => prev,
                  search: (prev) => prev,
                });
              },
            });
          }}
        >
          <TrashIcon />
          Delete
        </Button>
      </div>
      <IntegrationBody />
    </div>
  );
}

function IntegrationBody() {
  const integration = useIntegrationRecord();

  if (integration.type === IntegrationType.Jucy) {
    return <JucyIntegrationScreen integration={integration} />;
  }

  if (integration.type === IntegrationType.Rcm) {
    return <RcmIntegrationScreen integration={integration} />;
  }

  if (integration.type === IntegrationType.Camperman) {
    return <CampermanIntegrationScreen />;
  }

  if (integration.type === IntegrationType.CampermanGapFiller) {
    return <CampermanGapFillerIntegrationScreen />;
  }

  if (integration.type === IntegrationType.Roadsurfer) {
    return <RoadsurferIntegrationScreen integration={integration} />;
  }

  if (integration.type === IntegrationType.Click4Wheels) {
    return <Click4WheelsIntegrationScreen integration={integration} />;
  }

  if (integration.type === IntegrationType.GoogleSheets) {
    return <GoogleSheetsIntegrationScreen integration={integration} />;
  }

  throw new Error(`Integration type ${integration.type} not implemented`);
}
